/**
 * Public-site Visitor Intelligence `.track()` helper.
 * Region bootstrap exposes `window.__CMS_VISITOR_INTELLIGENCE__` when consent
 * allows it (`cms:visitor-intelligence:bootstrap`). No-ops when the tracker is
 * missing or not allowed — forms and buttons still work.
 *
 * First-party hops are batched (`page_view`, `cta_click`, `time_on_page`,
 * `scroll_depth`). Click heatmaps use a separate Region module — do not
 * enqueue heatmap points here.
 */

import { CUSTOM_CODE_ROOT_ATTR } from "./customCodeCssScope";

export const VISITOR_INTELLIGENCE_PUBLIC_BOOTSTRAP_EVENT =
	"cms:visitor-intelligence:bootstrap";

/** Set by published `button` `<a>` so the document listener does not double-fire. */
export const PUBLISHED_CTA_TRACKED_ATTR = "data-vi-cta";

export const SCROLL_DEPTH_MILESTONES = [25, 50, 75, 100] as const;
export const SCROLL_DEPTH_SHORT_PAGE_THRESHOLD_PX = 8;

const CTA_LABEL_MAX_LENGTH = 120;
const SKIP_CTA_HREF_SCHEME = /^(mailto:|tel:|javascript:)/i;
const ABSOLUTE_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const HEADER_CHROME_SELECTOR = [
	"header",
	"nav",
	'[data-global-slot="header"]',
	"[data-pb-mega-menu]",
	"[data-pb-advanced-mega-menu]",
].join(",");
const HEADER_ROOT_CLASS_TOKENS = [
	"classic-header",
	"mega-menu-header",
	"audience-mega-menu",
	"advanced-mega-menu",
] as const;

type PublicVisitorIntelligenceEventType =
	| "form_view"
	| "form_start"
	| "form_submit"
	| "form_abandon"
	| "cta_click"
	| "time_on_page"
	| "scroll_depth";

type VisitorIntelligencePublicTracker = {
	allowed?: boolean;
	flush?: (options?: { keepalive?: boolean }) => void;
	track?: (
		eventType: PublicVisitorIntelligenceEventType,
		payload?: Record<string, unknown>
	) => void;
};

type VisitorIntelligenceWindow = Window & {
	__CMS_VISITOR_INTELLIGENCE__?: VisitorIntelligencePublicTracker;
};

const viewedFormIds = new Set<string>();

function getVisitorIntelligencePublicTracker(): VisitorIntelligencePublicTracker | null {
	if (typeof window === "undefined") {
		return null;
	}
	const state = (window as VisitorIntelligenceWindow)
		.__CMS_VISITOR_INTELLIGENCE__;
	if (!(state?.allowed && typeof state.track === "function")) {
		return null;
	}
	return state;
}

export function trackVisitorIntelligenceEvent(
	eventType: PublicVisitorIntelligenceEventType,
	payload?: Record<string, unknown>
): void {
	const tracker = getVisitorIntelligencePublicTracker();
	tracker?.track?.(eventType, payload);
}

export function flushVisitorIntelligenceTracker(options?: {
	keepalive?: boolean;
}): void {
	const tracker = getVisitorIntelligencePublicTracker();
	tracker?.flush?.(options);
}

export function formJourneyEventData(params: {
	formId: string;
	formSlug?: string;
	submissionId?: string;
}): Record<string, string> {
	const formSlug = params.formSlug?.trim() ?? "";
	const submissionId = params.submissionId?.trim();
	return {
		form_id: params.formId,
		form_slug: formSlug,
		...(submissionId ? { submission_id: submissionId } : {}),
	};
}

/** `form_view` once per page + form id, when the tracker is allowed. */
export function trackFormViewOnce(params: {
	formId: string;
	formSlug?: string;
}): boolean {
	if (viewedFormIds.has(params.formId)) {
		return true;
	}
	if (!getVisitorIntelligencePublicTracker()) {
		return false;
	}
	viewedFormIds.add(params.formId);
	trackVisitorIntelligenceEvent(
		"form_view",
		formJourneyEventData({
			formId: params.formId,
			formSlug: params.formSlug,
		})
	);
	return true;
}

export function isTrackableCtaHref(href: string): boolean {
	const trimmed = href.trim();
	if (!trimmed) {
		return false;
	}
	if (SKIP_CTA_HREF_SCHEME.test(trimmed)) {
		return false;
	}
	if (ABSOLUTE_SCHEME.test(trimmed) && !/^https?:/i.test(trimmed)) {
		return false;
	}
	return true;
}

export function classifyPublishedCtaHref(href: string): {
	host?: string;
	outbound: boolean;
} {
	const trimmed = href.trim();
	if (!trimmed) {
		return { outbound: false };
	}
	const isRelative = !(
		ABSOLUTE_SCHEME.test(trimmed) || trimmed.startsWith("//")
	);
	const hasWindowOrigin =
		typeof window !== "undefined" &&
		typeof window.location?.href === "string" &&
		window.location.href.length > 0;
	const base = hasWindowOrigin
		? window.location.href
		: "https://internal.invalid/";
	try {
		const url = new URL(trimmed, base);
		if (url.protocol !== "http:" && url.protocol !== "https:") {
			return { outbound: false };
		}
		if (!hasWindowOrigin) {
			return isRelative
				? { outbound: false }
				: { host: url.host, outbound: true };
		}
		if (url.origin === window.location.origin) {
			return { outbound: false };
		}
		return { host: url.host, outbound: true };
	} catch {
		return { outbound: false };
	}
}

export function truncateCtaLabel(label: string | undefined): string {
	const text = typeof label === "string" ? label.trim() : "";
	if (text.length <= CTA_LABEL_MAX_LENGTH) {
		return text;
	}
	return text.slice(0, CTA_LABEL_MAX_LENGTH);
}

function currentFromPath(): string | undefined {
	if (typeof window === "undefined") {
		return undefined;
	}
	const path = window.location.pathname.trim();
	return path.length > 0 ? path : "/";
}

export function trackPublishedCtaClick(
	href: string,
	label: string | undefined
): void {
	if (!isTrackableCtaHref(href)) {
		return;
	}
	const ctaLabel = truncateCtaLabel(label);
	const fromPath = currentFromPath();
	const link = classifyPublishedCtaHref(href);
	trackVisitorIntelligenceEvent("cta_click", {
		href: href.trim(),
		outbound: link.outbound,
		...(link.host ? { host: link.host } : {}),
		...(ctaLabel ? { cta_label: ctaLabel } : {}),
		...(fromPath ? { from_path: fromPath } : {}),
	});
}

export function trackPublishedButtonCtaClick(
	href: string,
	label: string | undefined
): void {
	trackPublishedCtaClick(href, label);
}

function publishedCtaAnchorLabel(
	anchor: HTMLAnchorElement
): string | undefined {
	const fromText = truncateCtaLabel(anchor.textContent ?? undefined);
	if (fromText) {
		return fromText;
	}
	return truncateCtaLabel(anchor.getAttribute("aria-label") ?? undefined);
}

function elementHasHeaderRootClass(element: Element): boolean {
	const className = element.getAttribute("class");
	if (!className) {
		return false;
	}
	return HEADER_ROOT_CLASS_TOKENS.some((token) => className.includes(token));
}

function isHeaderOrMenuChromeAnchor(anchor: HTMLAnchorElement): boolean {
	if (anchor.closest(HEADER_CHROME_SELECTOR)) {
		return true;
	}
	let node: Element | null = anchor;
	while (node) {
		if (elementHasHeaderRootClass(node)) {
			return true;
		}
		node = node.parentElement;
	}
	return false;
}

function isDelegatedPublishedCtaAnchor(anchor: HTMLAnchorElement): boolean {
	if (anchor.hasAttribute(PUBLISHED_CTA_TRACKED_ATTR)) {
		return false;
	}
	if (anchor.closest(`[${CUSTOM_CODE_ROOT_ATTR}]`)) {
		return false;
	}
	if (anchor.classList.contains("interlink")) {
		return true;
	}
	if (anchor.closest("[data-pb-node]")) {
		return true;
	}
	return isHeaderOrMenuChromeAnchor(anchor);
}

function handlePublishedCtaDelegation(event: Event): void {
	if (!getVisitorIntelligencePublicTracker()) {
		return;
	}
	const eventTarget = event.target;
	if (!(eventTarget instanceof Element)) {
		return;
	}
	const anchor = eventTarget.closest("a[href]");
	if (!(anchor instanceof HTMLAnchorElement)) {
		return;
	}
	if (!isDelegatedPublishedCtaAnchor(anchor)) {
		return;
	}
	const href = anchor.getAttribute("href") ?? "";
	trackPublishedCtaClick(href, publishedCtaAnchorLabel(anchor));
}

let publishedCtaDelegationBound = false;

/** One document listener for HTML-string interlinks and other in-content `<a>`. */
export function bindPublishedCtaClickDelegation(): void {
	if (typeof document === "undefined" || publishedCtaDelegationBound) {
		return;
	}
	publishedCtaDelegationBound = true;
	document.addEventListener("click", handlePublishedCtaDelegation);
}

export function resetPublishedCtaClickDelegationForTests(): void {
	if (typeof document !== "undefined" && publishedCtaDelegationBound) {
		document.removeEventListener("click", handlePublishedCtaDelegation);
	}
	publishedCtaDelegationBound = false;
}

export function computeTimeOnPageSeconds(
	startedAtMs: number,
	endedAtMs: number
): number {
	if (!(Number.isFinite(startedAtMs) && Number.isFinite(endedAtMs))) {
		return 0;
	}
	const elapsedMs = endedAtMs - startedAtMs;
	if (elapsedMs <= 0) {
		return 0;
	}
	return Math.floor(elapsedMs / 1000);
}

export function measurePublishedScrollDepth(): {
	canScroll: boolean;
	percent: number;
} {
	if (typeof document === "undefined" || typeof window === "undefined") {
		return { canScroll: false, percent: 100 };
	}
	const root = document.documentElement;
	const viewport = window.innerHeight || root.clientHeight;
	const height = Math.max(
		root.scrollHeight,
		document.body?.scrollHeight ?? 0,
		viewport
	);
	const canScroll = height > viewport + SCROLL_DEPTH_SHORT_PAGE_THRESHOLD_PX;
	if (!canScroll) {
		return { canScroll: false, percent: 100 };
	}
	const scrollTop = window.scrollY || root.scrollTop;
	const seen = scrollTop + viewport;
	const percent = Math.min(100, Math.max(0, Math.round((seen / height) * 100)));
	return { canScroll: true, percent };
}

export function scrollDepthMilestonesToEmit(
	percent: number,
	canScroll: boolean,
	alreadyFired: ReadonlySet<number>
): number[] {
	if (!canScroll) {
		return alreadyFired.has(100) ? [] : [100];
	}
	return SCROLL_DEPTH_MILESTONES.filter(
		(milestone) => percent >= milestone && !alreadyFired.has(milestone)
	);
}

const firedScrollMilestones = new Set<number>();
let publishedScrollDepthBound = false;
let scrollDepthRaf = 0;

function emitReachedScrollDepths(): void {
	if (!getVisitorIntelligencePublicTracker()) {
		return;
	}
	const { canScroll, percent } = measurePublishedScrollDepth();
	const next = scrollDepthMilestonesToEmit(
		percent,
		canScroll,
		firedScrollMilestones
	);
	for (const milestone of next) {
		firedScrollMilestones.add(milestone);
		trackVisitorIntelligenceEvent("scroll_depth", { percent: milestone });
	}
}

function handlePublishedScrollDepth(): void {
	if (scrollDepthRaf !== 0) {
		return;
	}
	const schedule =
		typeof window !== "undefined" &&
		typeof window.requestAnimationFrame === "function"
			? window.requestAnimationFrame.bind(window)
			: (callback: FrameRequestCallback) =>
					window.setTimeout(() => callback(Date.now()), 250);
	scrollDepthRaf = schedule(() => {
		scrollDepthRaf = 0;
		emitReachedScrollDepths();
	});
}

/** One passive scroll listener. Call `resetPublishedScrollDepthMilestones` on pathname change. */
export function bindPublishedScrollDepthTracking(): void {
	if (typeof window === "undefined" || publishedScrollDepthBound) {
		return;
	}
	publishedScrollDepthBound = true;
	window.addEventListener("scroll", handlePublishedScrollDepth, {
		passive: true,
	});
}

export function resetPublishedScrollDepthMilestones(): void {
	firedScrollMilestones.clear();
	if (scrollDepthRaf !== 0 && typeof window !== "undefined") {
		if (typeof window.cancelAnimationFrame === "function") {
			window.cancelAnimationFrame(scrollDepthRaf);
		} else {
			window.clearTimeout(scrollDepthRaf);
		}
		scrollDepthRaf = 0;
	}
	emitReachedScrollDepths();
}

export function resetPublishedScrollDepthTrackingForTests(): void {
	if (typeof window !== "undefined" && publishedScrollDepthBound) {
		window.removeEventListener("scroll", handlePublishedScrollDepth);
	}
	publishedScrollDepthBound = false;
	firedScrollMilestones.clear();
	if (scrollDepthRaf !== 0 && typeof window !== "undefined") {
		if (typeof window.cancelAnimationFrame === "function") {
			window.cancelAnimationFrame(scrollDepthRaf);
		} else {
			window.clearTimeout(scrollDepthRaf);
		}
		scrollDepthRaf = 0;
	}
}
